import React, { useState, useEffect } from 'react';
import {
  Edit,
  SimpleForm,
  TextInput,
  BooleanInput,
  SelectInput,
  AutocompleteInput,
  ArrayInput,
  SimpleFormIterator,
  useRecordContext,
  FormDataConsumer
} from 'react-admin';
import {
  Box,
  Tabs,
  Tab,
  Paper,
  Typography
} from '@mui/material';
import { JsonSchemaTextInput } from './JsonSchemaTextInput';
import { SchemaVisualRenderer } from './SchemaVisualRenderer';
import EditIcon from '@mui/icons-material/Edit';
import CodeIcon from '@mui/icons-material/Code';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useDataModelHierarchy } from '../../hooks/useDataModelHierarchy';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`schema-tabpanel-${index}`}
      aria-labelledby={`schema-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `schema-tab-${index}`,
    'aria-controls': `schema-tabpanel-${index}`,
  };
}

export const SchemaEditor = () => {
  const [tabValue, setTabValue] = useState(0);
  const record = useRecordContext();
  const { domains } = useDataModelHierarchy();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Build domain choices from DataModel domains + defaults
  const domainChoices = React.useMemo(() => {
    const dataModelDomains = domains.map(d => ({ id: d.name.toLowerCase(), name: d.name }));
    const defaultDomains = [
      { id: 'commerce', name: 'Commerce' },
      { id: 'identity', name: 'Identity' },
      { id: 'location', name: 'Location' },
      { id: 'operations', name: 'Operations' },
      { id: 'framework', name: 'Framework' },
      { id: 'analytics', name: 'Analytics' },
      { id: 'general', name: 'General' },
    ];
    
    // Merge and deduplicate
    const seen = new Set();
    const merged = [...dataModelDomains, ...defaultDomains].filter(d => {
      if (seen.has(d.id)) return false;
      seen.add(d.id);
      return true;
    });
    
    return merged.sort((a, b) => a.name.localeCompare(b.name));
  }, [domains]);

  return (
    <Edit>
      <Box sx={{ width: '100%' }}>
        {/* Header */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5">
            Edit Schema: {record?.name || 'Schema'}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {record?.description || 'Schema definition'}
          </Typography>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="schema editor tabs">
            <Tab icon={<EditIcon />} label="Basic Info" {...a11yProps(0)} />
            <Tab icon={<CodeIcon />} label="JSON Editor" {...a11yProps(1)} />
            <Tab icon={<VisibilityIcon />} label="Visual Schema" {...a11yProps(2)} />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0}>
          {/* Basic Information Form */}
          <SimpleForm>
            <TextInput source="name" label="Name" required fullWidth />
            <TextInput source="key" label="Key" required fullWidth />
            <TextInput source="description" label="Description" multiline rows={3} fullWidth />
            
            <AutocompleteInput
              source="domain"
              label="Domain"
              choices={domainChoices}
              fullWidth
              helperText="Select from existing domains or type a new domain name"
              freeSolo
            />
            
            <SelectInput
              source="category"
              label="Category"
              choices={[
                { id: 'entity', name: 'Entity' },
              ]}
              defaultValue="entity"
              fullWidth
            />
            
            <BooleanInput source="configuration.isCore" label="Core Schema" />
            <BooleanInput source="configuration.allowExtensions" label="Allow Extensions" />
            <BooleanInput source="configuration.requiresSync" label="Requires Sync" />
            
            <ArrayInput source="configuration.tags" label="Tags">
              <SimpleFormIterator>
                <TextInput source="" label="Tag" />
              </SimpleFormIterator>
            </ArrayInput>
            
          </SimpleForm>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* JSON Editor - Full Screen */}
          <SimpleForm>
            <JsonSchemaTextInput 
              source="jsonSchema" 
              label="JSON Schema Definition" 
              fullScreen={true}
            />
          </SimpleForm>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {/* Visual Schema Renderer with live form data */}
          <Box sx={{ width: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Schema Visualization
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }} color="textSecondary">
              Visual representation of the schema structure and relationships.
            </Typography>
            
            <FormDataConsumer>
              {({ formData }) => (
                <SchemaVisualRenderer 
                  jsonSchema={formData?.jsonSchema || record?.jsonSchema}
                />
              )}
            </FormDataConsumer>
          </Box>
        </TabPanel>
      </Box>
    </Edit>
  );
};

